import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Store, MapPin, CalendarDays, Clock, Home } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import Logo from "../components/portal/Logo";
import FeedbackPrompt from "../components/portal/FeedbackPrompt";

export default function PickupSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state;

  useEffect(() => {
    if (!data) navigate("/", { replace: true });
  }, [data, navigate]);

  if (!data) return null;

  const dateObj = parseISO(data.pickup_date);
  const prettyDate = isValid(dateObj) ? format(dateObj, "d MMM yyyy") : data.pickup_date;

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-line bg-ivory/70 backdrop-blur">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-5 sm:px-8 py-5">
          <Logo />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-center mb-8">
            <div className="inline-flex h-16 w-16 rounded-full bg-maroon/[0.08] text-maroon items-center justify-center mb-5">
              <CheckCircle2 size={32} strokeWidth={1.4} />
            </div>
            <p className="overline text-[10px] mb-2">Pickup Scheduled</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
              Your in-store pickup has been scheduled successfully.
            </h1>
          </div>

          <div className="bg-white border border-line rounded-2xl p-6 sm:p-8 space-y-5" data-testid="pickup-summary">
            <Row label="Invoice" value={data.invoice_number} />
            <Row label="Name" value={data.customer_name} />
            <Row label="Pickup Date" value={prettyDate} icon={CalendarDays} />
            <Row label="Preferred Time" value={data.pickup_slot} icon={Clock} />
            {data.store && (
              <div className="pt-4 border-t border-line">
                <p className="overline text-[10px] mb-2">Location</p>
                <div className="flex items-start gap-3">
                  <Store size={18} strokeWidth={1.5} className="text-maroon flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-serif text-lg text-ink leading-tight">{data.store.name}</p>
                    <p className="text-sm text-muted2 mt-1 leading-relaxed">
                      {data.store.address_line1}<br />
                      {data.store.address_line2}
                    </p>
                    <a
                      href={data.store.maps_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-maroon hover:underline"
                    >
                      <MapPin size={13} strokeWidth={1.6} /> Open in Google Maps
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-muted2 mt-8 leading-relaxed italic">
            We look forward to welcoming you at our store.
          </p>

          <p
            data-testid="pickup-whatsapp-note"
            className="text-center text-xs text-muted2 mt-3 leading-relaxed"
          >
            {data.whatsapp_scheduled
              ? "A pickup confirmation has been sent to your WhatsApp."
              : "Our team will reach out with any updates."}
          </p>

          <FeedbackPrompt submissionId={data.submission_id} group={data.group_type} />

          <div className="text-center mt-8">
            <button
              data-testid="pickup-home-btn"
              onClick={() => navigate("/")}
              className="btn-outline-maroon inline-flex items-center gap-2 !text-xs !px-5 !py-2.5"
            >
              <Home size={13} strokeWidth={1.6} /> Return home
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

const Row = ({ label, value, icon: Icon }) => (
  <div className="flex items-start justify-between gap-4">
    <p className="overline text-[10px] flex items-center gap-2">
      {Icon && <Icon size={12} strokeWidth={1.5} className="text-muted2" />}{label}
    </p>
    <p className="text-sm text-ink text-right font-medium">{value}</p>
  </div>
);
